import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE_URL } from "../config";
import { useCurrency } from "../context/CurrencyContext";

const DEFAULT_IMG =
  "https://westendjewellery.com/assets/Images/uatproduct/default.png";

// SAFE JSON Helper
const safeJSON = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return fallback;
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const sameText = (a, b) =>
  (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

// FIX: Set() is case-sensitive, so "Earring" and "EARRING" showed up as two
// separate dropdown options. This de-duplicates ignoring case/extra spaces and
// keeps the first spelling it sees (optionally passed through `format`).
const uniqueIgnoreCase = (values, format = (v) => v) => {
  const seen = new Map();
  values.forEach((v) => {
    const text = (v || "").trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (!seen.has(key)) seen.set(key, format(text));
  });
  return [...seen.values()];
};

// "EARRING" / "earring" -> "Earring"
const toTitleCase = (s) =>
  s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/* Image with loading placeholder + fallback when the URL fails */
function SafeImage({ src, alt, className }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // reset whenever the image changes (pagination / filters)
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  return (
    <>
      {!loaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "#f2f2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "#999",
            zIndex: 1,
          }}
        >
          Loading...
        </div>
      )}
      <img
        src={failed ? DEFAULT_IMG : src}
        alt={alt}
        className={className}
        onLoad={() => setLoaded(true)}
        onError={() => {
          // fall back once only, to avoid an infinite error loop
          if (!failed) setFailed(true);
          else setLoaded(true);
        }}
      />
    </>
  );
}

/* Reusable dropdown (same look as your existing filters) */
function FilterDropdown({ label, value, options, onSelect, padding = "3px 0" }) {
  return (
    <div className="products-sort dropdown" style={{ display: "inline-block", position: "relative" }}>
      <span
        className="sort-toggle dropdown-toggle"
        data-toggle="dropdown"
        style={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
          color: "#333",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "6px",
          cursor: "pointer",
          boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
          userSelect: "none",
        }}
      >
        {value === "ALL" ? label : value}
      </span>

      <ul className="sort-list dropdown-menu" style={{ padding }}>
        {options.map((opt) => {
          const isActive = value === opt;
          return (
            <li key={opt} className={isActive ? "active" : ""} style={{ margin: "2px 0" }}>
              <a
                href="#0"
                onClick={() => onSelect(opt)}
                style={{
                  display: "block",
                  padding: "5px 10px",
                  fontSize: "14px",
                  color: isActive ? "#fff" : "#444",
                  backgroundColor: isActive ? "#ae9369" : "transparent",
                  fontWeight: isActive ? "600" : "400",
                  cursor: "pointer",
                }}
              >
                {opt}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function StockMaster() {
  const { selectedCurrency } = useCurrency();

  const [stockList, setStockList] = useState([]);
  const [loading, setLoading] = useState(true); // start as true so "No stock found" never flashes
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [subCategoryFilter, setSubCategoryFilter] = useState("ALL");
  const [MetalFilter, setMetalFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [, setViewVersion] = useState(0);

  // FIX: removed the effect that set document.body.style.overflow = "hidden".
  // It locked page scrolling on this screen, and its cleanup ("auto") left a
  // stray inline style on <body> that could cause a double scrollbar elsewhere.

  useEffect(() => {
    const handleStorageChange = () => setViewVersion((v) => v + 1);
    const handleViewChange = () => setViewVersion((v) => v + 1);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("viewChange", handleViewChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("viewChange", handleViewChange);
    };
  }, []);

  const fetchStockList = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetch(`${API_BASE_URL}/InStock/GetAllStock`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStockList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Stock load failed:", err);
      setStockList([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockList();
  }, []);

  const getImageArray = (imagePath) => {
    if (!imagePath) return [];
    return imagePath
      .split(",")
      .map((img) => img.trim())
      .filter(Boolean)
      .map((img) => encodeURI(img));
  };

  // Categories and Metals (FIX: de-duplicated ignoring case, so
  // "Earring" / "EARRING" appear only once)
  const categories = [
    "ALL",
    ...uniqueIgnoreCase(
      stockList.map((item) => item.category),
      toTitleCase
    ).sort((a, b) => a.localeCompare(b)),
  ];

  const metalskt = [
    "ALL",
    ...uniqueIgnoreCase(stockList.map((item) => item.kt)),
  ];

  // Sub categories: only those that belong to the selected category
  const subCategories = [
    "ALL",
    ...uniqueIgnoreCase(
      stockList
        .filter((item) => categoryFilter === "ALL" || sameText(item.category, categoryFilter))
        .map((item) => item.subCategory),
      toTitleCase
    ).sort((a, b) => a.localeCompare(b)),
  ];

  /* FILTERED DATA */
  const filteredData = stockList.filter((item) => {
    const matchesCategory =
      categoryFilter === "ALL" || sameText(item.category, categoryFilter);

    const matchesSubCategory =
      subCategoryFilter === "ALL" || sameText(item.subCategory, subCategoryFilter);

    const matchesMetal = MetalFilter === "ALL" || sameText(item.kt, MetalFilter);

    const searchTarget = [item.styleNo, item.category, item.subCategory, item.kt]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !search.trim() || searchTarget.includes(search.trim().toLowerCase());

    return matchesCategory && matchesSubCategory && matchesMetal && matchesSearch;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, subCategoryFilter, MetalFilter, stockList]);

  const handleCategorySelect = (cat) => {
    setCategoryFilter(cat);
    setSubCategoryFilter("ALL"); // sub category list depends on category
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const visiblePages = 5;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) endPage = Math.min(visiblePages, totalPages);
    if (currentPage >= totalPages - 2) startPage = Math.max(1, totalPages - visiblePages + 1);

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) pages.push(i);

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const applyMarkup = (base) => {
    const view = localStorage.getItem("activeView") || "wholesale";
    if (view === "wholesale") return base;

    const storedPayload = safeJSON("priceTable_seller", {});
    const table = Array.isArray(storedPayload.data) ? storedPayload.data : [];

    if (!table.length) return base;

    const range = table.find((r) => {
      const from = parseFloat(r.from || 0);
      const to = r.to === null ? Infinity : parseFloat(r.to);
      return base >= from && base <= to;
    });

    if (!range) return base;

    const markupPercent = parseFloat(range.markup || 1);
    return base * markupPercent;
  };

  return (
    <div className="home">
      <Header />

      <div className="container-fluid my-3">
        <h5 className="stock_title">Australia Stock On Hand</h5>
        <hr />

        <div className="card mt-3">
          <div className="card-body p-2">
            {/* TOP BAR */}
            <div className="stocktext_input justify-content-between mb-2">
              <p className="total_stock_text">
                Total Stock Available :
                <span className="count_stock">
                  {" "}
                  {loading ? "..." : filteredData.length}
                </span>
              </p>

              <div className="sort_search">
                {/* METAL FILTER */}
                <FilterDropdown
                  label="Filter by Metal"
                  value={MetalFilter}
                  options={metalskt}
                  onSelect={setMetalFilter}
                  padding="6px 0"
                />

                {/* CATEGORY FILTER */}
                <FilterDropdown
                  label="Filter by Category"
                  value={categoryFilter}
                  options={categories}
                  onSelect={handleCategorySelect}
                />

                {/* SUB CATEGORY FILTER */}
                <FilterDropdown
                  label="Filter by Sub Category"
                  value={subCategoryFilter}
                  options={subCategories}
                  onSelect={setSubCategoryFilter}
                />

                {/* SEARCH */}
                <input
                  style={{ width: 200 }}
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <br />

            {/* PRODUCTS GRID */}
            <div className="products-list grid">
              <div className="row">
                {loading && (
                  <p className="text-center w-100">Loading stock...</p>
                )}

                {!loading && loadError && (
                  <div className="text-center w-100">
                    <p className="text-danger">Could not load stock.</p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={fetchStockList}
                    >
                      Try again
                    </button>
                  </div>
                )}

                {!loading && !loadError && filteredData.length === 0 && (
                  <p className="text-center text-muted w-100">No stock found.</p>
                )}

                {!loading &&
                  paginatedData.map((row, index) => {
                    const images = getImageArray(row.imagePath);

                    const firstImg = images[0] || DEFAULT_IMG;
                    const secondImg = images[1] || firstImg;

                    const basePrice =
                      (row.finalPrice || 0) * (selectedCurrency?.price || 1);

                    const finalCalculatedPrice = applyMarkup(basePrice).toFixed(0);

                    return (
                      <div
                        key={`${row.styleNo}_${index}`}
                        className="col-xl-3 col-lg-4 col-md-4 col-sm-6 mb-4"
                      >
                        <div className="products-entry clearfix product-wapper">
                          {/* aspectRatio reserves space so the card never collapses while loading */}
                          <div
                            className="products-thumb"
                            style={{ position: "relative", aspectRatio: "1 / 1" }}
                          >
                            <div className="product-thumb-hover">
                              <Link to={`/stockcarryview/${row.styleNo}`}>
                                <SafeImage
                                  src={firstImg}
                                  className="post-image"
                                  alt={row.styleNo}
                                />
                                <SafeImage
                                  src={secondImg}
                                  className="hover-image back"
                                  alt={row.styleNo}
                                />
                              </Link>
                            </div>
                          </div>

                          <div className="product-button" id="gold-div">
                            <div className="stone-option-container">
                              <p className="selected-stone-text">
                                <strong>{row.styleNo}</strong>
                              </p>

                              <p className="selected-stone-text">
                                {selectedCurrency?.name || "$"} {finalCalculatedPrice}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {totalPages > 1 && (
                <div className="pagination flex justify-center mt-4 mb-2">
                  <ul className="flex flex-wrap items-center gap-2 list-unstyled p-0 m-0">
                    <li>
                      <button
                        type="button"
                        className={`pageBtns ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>
                    </li>

                    {getPageNumbers().map((page, idx) => (
                      <li key={idx}>
                        {page === "..." ? (
                          <span
                            className="pageNum"
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "default",
                              padding: "8px 10px",
                            }}
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={`pageNum ${currentPage === page ? "active" : ""}`}
                            onClick={() => handlePageChange(page)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "1px solid #e0e0e0",
                              fontSize: "14px",
                              fontWeight: currentPage === page ? "600" : "400",
                              color: currentPage === page ? "#fff" : "#444",
                              backgroundColor: currentPage === page ? "#93918d" : "#fff",
                              cursor: "pointer",
                            }}
                          >
                            {page}
                          </button>
                        )}
                      </li>
                    ))}

                    <li>
                      <button
                        type="button"
                        className={`pageBtns ${currentPage === totalPages ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}